import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { UserStatus, UserStatusWithUser, CreateUserStatusRequest, GetUserStatusesQuery, GetStatusesByLocationQuery, NewStatusAnimation } from '../types';
import apiService from '../services/api';
import { useLocationStore } from './locationStore';
import { getErrorMessage } from '../utils/errors';
import { useAuthStore } from './authStore';
import { MarkerArrivalEffect, markerAnimationDuration, randomMarkerArrivalEffect } from '../utils/markerArrivalEffects';

interface UserStatusState {
  // State
  userStatuses: UserStatus[];
  nearbyStatuses: UserStatusWithUser[];
  mapStatuses: UserStatusWithUser[];
  animatedStatusIds: Set<number>;
  animatedStatusEffects: Map<number, MarkerArrivalEffect>;
  collectedEmojis: Set<string>;
  isLoading: boolean;
  isFetchingUserStatuses: boolean;
  isFetchingNearbyStatuses: boolean;
  isCreatingStatus: boolean;
  likingStatusIds: Set<number>;
  liveLikes: Record<number, { likesCount: number; isLikedByCurrentUser?: boolean }>;
  error: string | null;
  mutationError: string | null;

  // Actions
  createStatus: (data: CreateUserStatusRequest) => Promise<UserStatus>;
  fetchUserStatuses: (userId: number, query?: GetUserStatusesQuery) => Promise<UserStatus[]>;
  fetchPublicUserStatuses: (userId: number, query?: GetUserStatusesQuery) => Promise<UserStatus[]>;
  fetchNearbyStatuses: (query: GetStatusesByLocationQuery) => Promise<void>;
  fetchStatusesByCoordinates: (query: GetStatusesByLocationQuery) => Promise<void>;
  fetchStatusesForMap: (query: GetStatusesByLocationQuery) => Promise<UserStatusWithUser[]>;
  fetchStatus: (statusId: number) => Promise<void>;
  likeStatus: (statusId: number) => Promise<{ likesCount: number; isLikedByCurrentUser: boolean }>;
  receiveStatusLike: (statusId: number, userId: number, likesCount: number, isLiked: boolean) => void;
  clearError: () => void;
  clearMutationError: () => void;
  clearUserStatuses: () => void;
  clearNearbyStatuses: () => void;
  clearMapStatuses: () => void;
  addCollectedEmojis: (emojis: string[]) => void;
  addMapStatus: (status: UserStatusWithUser, animation: NewStatusAnimation) => void;
  stopStatusAnimation: (statusId: number) => void;
  incrementCommentsCount: (statusId: number) => void;
  setCommentsCount: (statusId: number, commentsCount: number) => void;
}

export const useUserStatusStore = create<UserStatusState>()(
  subscribeWithSelector((set, get) => {
  let latestUserStatusesRequest = 0;
  let latestNearbyStatusesRequest = 0;
  let latestMapStatusesRequest = 0;
  const animationTimers = new Map<number, ReturnType<typeof setTimeout>>();

  const clearAnimationTimer = (statusId: number) => {
    const timer = animationTimers.get(statusId);
    if (timer) clearTimeout(timer);
    animationTimers.delete(statusId);
  };

  return ({
  // Initial state
  userStatuses: [],
  nearbyStatuses: [],
  mapStatuses: [],
  animatedStatusIds: new Set(),
  animatedStatusEffects: new Map(),
  collectedEmojis: new Set(),
  isLoading: false,
  isFetchingUserStatuses: false,
  isFetchingNearbyStatuses: false,
  isCreatingStatus: false,
  likingStatusIds: new Set(),
  liveLikes: {},
  error: null,
  mutationError: null,

  // Create a new status
  createStatus: async (data: CreateUserStatusRequest) => {
    set({ isCreatingStatus: true, error: null });
    try {
      const newStatus = await apiService.createUserStatus(data);
      const currentUser = useAuthStore.getState().user;
      const mapStatus = currentUser && newStatus.latitude != null && newStatus.longitude != null
        ? { ...newStatus, user: currentUser }
        : null;

      // Reflect a successful post immediately in both the profile and live map.
      set((state) => ({
        userStatuses: [newStatus, ...state.userStatuses],
        mapStatuses: mapStatus
          ? [mapStatus, ...state.mapStatuses.filter((status) => status.id !== newStatus.id)]
          : state.mapStatuses,
        isCreatingStatus: false,
        error: null
      }));
      return newStatus;
    } catch (error: unknown) {
      set({
        isCreatingStatus: false,
        error: getErrorMessage(error, 'Failed to create status')
      });
      throw error;
    }
  },

  // Fetch statuses for a specific user
  fetchUserStatuses: async (userId: number, query?: GetUserStatusesQuery) => {
    const requestId = ++latestUserStatusesRequest;
    set({ isLoading: true, isFetchingUserStatuses: true, error: null });
    try {
      const statuses = await apiService.getUserStatuses(userId, query);
      if (requestId === latestUserStatusesRequest) {
        set({ userStatuses: statuses, isLoading: false, isFetchingUserStatuses: false, error: null });
      }

      return statuses; // Return the statuses
    } catch (error: unknown) {
      if (requestId === latestUserStatusesRequest) {
        set({
          isLoading: false,
          isFetchingUserStatuses: false,
          error: getErrorMessage(error, 'Failed to fetch user statuses')
        });
      }
      throw error;
    }
  },

  fetchPublicUserStatuses: async (userId: number, query?: GetUserStatusesQuery) => {
    return apiService.getPublicUserStatuses(userId, query);
  },

  // Fetch nearby statuses based on current user's location or provided coordinates
  fetchNearbyStatuses: async (query: GetStatusesByLocationQuery) => {
    const requestId = ++latestNearbyStatusesRequest;
    set({ isLoading: true, isFetchingNearbyStatuses: true, error: null });
    try {
      let latitude = query.latitude;
      let longitude = query.longitude;
      // If latitude/longitude not provided, fall back to userCoordinates
      if (latitude === undefined || longitude === undefined) {
        const locationStore = useLocationStore.getState();
        let userCoordinates = locationStore.userCoordinates;
        if (!userCoordinates) {
          await locationStore.fetchUserLocation();
          userCoordinates = useLocationStore.getState().userCoordinates;
          if (!userCoordinates) {
            try {
              const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  timeout: 10000,
                  maximumAge: 300000 // 5 minutes
                });
              });
              await locationStore.updateUserLocation(
                position.coords.latitude,
                position.coords.longitude
              );
              userCoordinates = useLocationStore.getState().userCoordinates;
            } catch (geoError) {
              throw new Error('Unable to get your location. Please enable location services or update your location manually.');
            }
          }
        }
        if (!userCoordinates) {
          throw new Error('No location data available. Please update your location first.');
        }
        latitude = userCoordinates.latitude;
        longitude = userCoordinates.longitude;
      }
      // Use the coordinates from query or fallback
      const queryWithCoords = {
        ...query,
        latitude,
        longitude,
      };
      const statuses = useAuthStore.getState().isAuthenticated
        ? await apiService.getStatusesByLocation(queryWithCoords)
        : await apiService.getPublicStatusesByCoordinates(queryWithCoords);
      if (requestId === latestNearbyStatusesRequest) {
        set({ nearbyStatuses: statuses, isLoading: false, isFetchingNearbyStatuses: false, error: null });
      }
      // Collect emojis
      const emojis = statuses.map(s => s.user.avatarEmoji).filter((e): e is string => e != null && e.trim() !== '');
      if (emojis.length > 0) {
        get().addCollectedEmojis(emojis);
      }
    } catch (error: unknown) {
      if (requestId === latestNearbyStatusesRequest) {
        set({
          isLoading: false,
          isFetchingNearbyStatuses: false,
          error: getErrorMessage(error, 'Failed to fetch nearby statuses')
        });
      }
      throw error;
    }
  },

  // Fetch statuses by specific coordinates
  fetchStatusesByCoordinates: async (query: GetStatusesByLocationQuery) => {
    const requestId = ++latestNearbyStatusesRequest;
    set({ isLoading: true, isFetchingNearbyStatuses: true, error: null });
    try {
      const statuses = await apiService.getStatusesByCoordinates(query);
      if (requestId === latestNearbyStatusesRequest) {
        set({ nearbyStatuses: statuses, isLoading: false, isFetchingNearbyStatuses: false, error: null });
      }
      // Collect emojis
      const emojis = statuses.map(s => s.user.avatarEmoji).filter((e): e is string => e != null && e.trim() !== '');
      if (emojis.length > 0) {
        get().addCollectedEmojis(emojis);
      }
    } catch (error: unknown) {
      if (requestId === latestNearbyStatusesRequest) {
        set({
          isLoading: false,
          isFetchingNearbyStatuses: false,
          error: getErrorMessage(error, 'Failed to fetch statuses by coordinates')
        });
      }
      throw error;
    }
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  },

  clearMutationError: () => {
    set({ mutationError: null });
  },

  // Clear user statuses
  clearUserStatuses: () => {
    set({ userStatuses: [] });
  },

  // Clear nearby statuses
  clearNearbyStatuses: () => {
    set({ nearbyStatuses: [] });
  },

  // Fetch statuses for map display
  fetchStatusesForMap: async (query: GetStatusesByLocationQuery) => {
    const requestId = ++latestMapStatusesRequest;
    try {
      const statuses = await apiService.getStatusesByCoordinates(query);
      if (requestId === latestMapStatusesRequest) set({ mapStatuses: statuses });
      // Collect emojis
      const emojis = statuses.map(s => s.user.avatarEmoji).filter((e): e is string => e != null && e.trim() !== '');
      if (emojis.length > 0) {
        get().addCollectedEmojis(emojis);
      }
      return statuses;
    } catch (error: unknown) {
      console.error('Failed to fetch statuses for map:', error);
      throw error;
    }
  },

  // Fetch a single status and update it in the store
  fetchStatus: async (statusId: number) => {
    try {
      const updatedStatus = await apiService.getStatus(statusId);

      // Update the status in all relevant arrays
      set((state) => ({
        userStatuses: state.userStatuses.map(status =>
          status.id === statusId ? updatedStatus : status
        ),
        nearbyStatuses: state.nearbyStatuses.map(status =>
          status.id === statusId ? updatedStatus : status
        ),
        mapStatuses: state.mapStatuses.map(status =>
          status.id === statusId ? updatedStatus : status
        ),
      }));
    } catch (error: unknown) {
      console.error('Failed to fetch status:', error);
      throw error;
    }
  },

  // Like a status
  likeStatus: async (statusId: number) => {
    set((state) => ({ likingStatusIds: new Set(state.likingStatusIds).add(statusId), mutationError: null }));
    try {
      const result = await apiService.likeStatus(statusId);

      // Update the status in all relevant arrays
      set((state) => ({
        userStatuses: state.userStatuses.map(status =>
          status.id === statusId
            ? { ...status, likesCount: result.likesCount, isLikedByCurrentUser: result.isLikedByCurrentUser }
            : status
        ),
        nearbyStatuses: state.nearbyStatuses.map(status =>
          status.id === statusId
            ? { ...status, likesCount: result.likesCount, isLikedByCurrentUser: result.isLikedByCurrentUser }
            : status
        ),
        mapStatuses: state.mapStatuses.map(status =>
          status.id === statusId
            ? { ...status, likesCount: result.likesCount, isLikedByCurrentUser: result.isLikedByCurrentUser }
            : status
        ),
        liveLikes: {
          ...state.liveLikes,
          [statusId]: {
            likesCount: result.likesCount,
            isLikedByCurrentUser: result.isLikedByCurrentUser,
          },
        },
        mutationError: null
      }));

      return result;
    } catch (error: unknown) {
      set({ mutationError: getErrorMessage(error, 'Failed to like status') });
      throw error;
    } finally {
      set((state) => {
        const likingStatusIds = new Set(state.likingStatusIds);
        likingStatusIds.delete(statusId);
        return { likingStatusIds };
      });
    }
  },

  receiveStatusLike: (statusId, userId, likesCount, isLiked) => {
    const isCurrentUser = useAuthStore.getState().user?.id === userId;
    set((state) => {
      const update = <T extends UserStatus>(status: T): T => status.id === statusId
        ? {
            ...status,
            likesCount,
            isLikedByCurrentUser: isCurrentUser ? isLiked : status.isLikedByCurrentUser,
          }
        : status;
      const previous = state.liveLikes[statusId];
      return {
        userStatuses: state.userStatuses.map(update),
        nearbyStatuses: state.nearbyStatuses.map(update),
        mapStatuses: state.mapStatuses.map(update),
        liveLikes: {
          ...state.liveLikes,
          [statusId]: {
            likesCount,
            isLikedByCurrentUser: isCurrentUser
              ? isLiked
              : previous?.isLikedByCurrentUser,
          },
        },
      };
    });
  },

  // Clear map statuses
  clearMapStatuses: () => {
    animationTimers.forEach((timer) => clearTimeout(timer));
    animationTimers.clear();
    set({ mapStatuses: [], animatedStatusIds: new Set(), animatedStatusEffects: new Map() });
  },

  // Add collected emojis
  addCollectedEmojis: (emojis: string[]) => {
    set(state => {
      const collectedEmojis = new Set(state.collectedEmojis);
      emojis.forEach(emoji => collectedEmojis.add(emoji));
      return { collectedEmojis };
    });
  },

  // Merge a live status (e.g. from the WebSocket) into the map, avoiding duplicates.
  addMapStatus: (status: UserStatusWithUser, animation: NewStatusAnimation) => {
    clearAnimationTimer(status.id);
    set((state) => {
      const animatedStatusIds = new Set(state.animatedStatusIds);
      const animatedStatusEffects = new Map(state.animatedStatusEffects);
      if (animation === NewStatusAnimation.NEVER) {
        animatedStatusIds.delete(status.id);
        animatedStatusEffects.delete(status.id);
      } else {
        animatedStatusIds.add(status.id);
        animatedStatusEffects.set(status.id, randomMarkerArrivalEffect());
      }
      return {
        mapStatuses: [status, ...state.mapStatuses.filter((s) => s.id !== status.id)],
        animatedStatusIds,
        animatedStatusEffects,
      };
    });

    const duration = markerAnimationDuration(animation);
    if (duration !== null) {
      animationTimers.set(status.id, setTimeout(() => {
        animationTimers.delete(status.id);
        get().stopStatusAnimation(status.id);
      }, duration));
    }
  },
  stopStatusAnimation: (statusId: number) => {
    clearAnimationTimer(statusId);
    set((state) => {
      if (!state.animatedStatusIds.has(statusId)) return state;
      const animatedStatusIds = new Set(state.animatedStatusIds);
      const animatedStatusEffects = new Map(state.animatedStatusEffects);
      animatedStatusIds.delete(statusId);
      animatedStatusEffects.delete(statusId);
      return { animatedStatusIds, animatedStatusEffects };
    });
  },
  incrementCommentsCount: (statusId: number) => {
    set((state) => ({
      userStatuses: state.userStatuses.map((status) => status.id === statusId ? { ...status, commentsCount: (status.commentsCount ?? 0) + 1 } : status),
      nearbyStatuses: state.nearbyStatuses.map((status) => status.id === statusId ? { ...status, commentsCount: (status.commentsCount ?? 0) + 1 } : status),
      mapStatuses: state.mapStatuses.map((status) => status.id === statusId ? { ...status, commentsCount: (status.commentsCount ?? 0) + 1 } : status),
    }));
  },
  setCommentsCount: (statusId: number, commentsCount: number) => {
    const update = <T extends UserStatus>(status: T): T => status.id === statusId
      ? { ...status, commentsCount: Math.max(status.commentsCount ?? 0, commentsCount) }
      : status;
    set((state) => ({
      userStatuses: state.userStatuses.map(update),
      nearbyStatuses: state.nearbyStatuses.map(update),
      mapStatuses: state.mapStatuses.map(update),
    }));
  },
  });
})
);

// Selectors for selective subscriptions
export const useMapStatuses = () => useUserStatusStore(state => state.mapStatuses);
export const useNearbyStatuses = () => useUserStatusStore(state => state.nearbyStatuses);
export const useUserStatuses = () => useUserStatusStore(state => state.userStatuses);
export const useStatusLoading = () => useUserStatusStore(state => state.isLoading);
export const useStatusError = () => useUserStatusStore(state => state.error);
